import { useState, useEffect } from 'react';
import { Wallet, ArrowLeftRight, BookOpen, Settings2, LayoutDashboard, Cog, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import CointrackerOverview from './cointracker/Overview';
import CointrackerWallets from './cointracker/Wallets';
import CointrackerTransactions from './cointracker/Transactions';
import CointrackerJournalEntries from './cointracker/JournalEntries';
import CointrackerRules from './cointracker/Rules';
import CointrackerSettings from './cointracker/Settings';
import ChartOfAccounts from './cointracker/ChartOfAccounts';

const TABS = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'wallets', label: 'Wallets', icon: Wallet },
  { id: 'transactions', label: 'Transactions', icon: ArrowLeftRight },
  { id: 'journal', label: 'Journal Entries', icon: BookOpen },
  { id: 'accounts', label: 'Chart of Accounts', icon: Building2 },
  { id: 'rules', label: 'Rules', icon: Cog },
  { id: 'settings', label: 'Settings', icon: Settings2 },
];

export default function Cointracker() {
  const [activeTab, setActiveTab] = useState('overview');

  // Handle Xero OAuth callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const xeroStatus = urlParams.get('xero');
    
    if (xeroStatus === 'connected') {
      toast.success('Xero connected successfully! You can now sync journal entries.');
      setActiveTab('settings');
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return <CointrackerOverview />;
      case 'wallets':
        return <CointrackerWallets />;
      case 'transactions':
        return <CointrackerTransactions />;
      case 'journal':
        return <CointrackerJournalEntries />;
      case 'accounts':
        return <ChartOfAccounts />;
      case 'rules':
        return <CointrackerRules />;
      case 'settings':
        return <CointrackerSettings />;
      default:
        return <CointrackerOverview />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-4xl md:text-5xl font-bold font-heading text-white tracking-tight" data-testid="cointracker-title">
          Cointracker
        </h1>
        <p className="text-base text-muted-foreground mt-2">Track your crypto wallets and manage accounting</p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-zinc-800">
        <nav className="flex gap-1 -mb-px overflow-x-auto" data-testid="cointracker-tabs">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  isActive
                    ? 'border-white text-white'
                    : 'border-transparent text-zinc-400 hover:text-white hover:border-zinc-600'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Content */}
      <div data-testid="cointracker-content">
        {renderContent()}
      </div>
    </div>
  );
}
